// src/pages/Tickets/TicketStatusHistoryList.jsx
// -----------------------------------------------------------------------------
// Admin / PM screen for Ticket_Status_History_TBL.
// Full CRUD operations built with TableList, TableForm, DeleteConfirmModal,
// and custom hook useTableData.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import ticketStatusHistoryService from '../../api/ticketStatusHistoryService';
import ticketService, { mockStatuses } from '../../api/ticketService';
import userService from '../../api/userService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';

// Fallback mock lists if services fail or return empty (offline support)
const FALLBACK_USERS = [
  { userID: 1, username: 'johndoe', firstName: 'John', lastName: 'Doe' },
  { userID: 2, username: 'janesmith', firstName: 'Jane', lastName: 'Smith' },
];

const FALLBACK_TICKETS = [
  { ticketID: 1, ticketNumber: 'TCK-001', title: 'Login Page Layout alignment issue' },
  { ticketID: 2, ticketNumber: 'TCK-002', title: 'Implement Dark Mode theme options' },
];

export default function TicketStatusHistoryList() {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);

  // Initialize CRUD Hook
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(ticketStatusHistoryService, {
    idKey: 'historyID',
    itemName: 'Status History',
    initialSort: { key: 'historyID', direction: 'desc' },
  });

  // Load dropdown lists on mount
  useEffect(() => {
    ticketService
      .getAll()
      .then((res) => {
        const ticketList = res.data || [];
        setTickets(ticketList.length > 0 ? ticketList : FALLBACK_TICKETS);
      })
      .catch(() => setTickets(FALLBACK_TICKETS));

    userService
      .getAll()
      .then((res) => {
        const userList = res.data || [];
        setUsers(userList.length > 0 ? userList : FALLBACK_USERS);
      })
      .catch(() => setUsers(FALLBACK_USERS));
  }, []);

  // Form Fields Configuration
  const historyFields = useMemo(() => {
    const isEditMode = formMode === 'edit';
    const fields = [
      {
        name: 'ticketID',
        label: 'Ticket',
        type: 'select',
        required: true,
        options: tickets.map((t) => ({
          label: `${t.ticketNumber} — ${t.title}`,
          value: t.ticketID,
        })),
      },
      {
        name: 'fromStatusID',
        label: 'From Status',
        type: 'select',
        required: true,
        options: mockStatuses.map((s) => ({
          label: s.statusName,
          value: s.statusID,
        })),
      },
      {
        name: 'toStatusID',
        label: 'To Status',
        type: 'select',
        required: true,
        options: mockStatuses.map((s) => ({
          label: s.statusName,
          value: s.statusID,
        })),
      },
      {
        name: 'changedBy',
        label: 'Changed By',
        type: 'select',
        required: true,
        options: users.map((u) => ({
          label: `${u.firstName || ''} ${u.lastName || ''} (${u.username})`.trim(),
          value: u.userID,
        })),
      },
    ];

    if (isEditMode) {
      fields.unshift({
        name: 'historyID',
        label: 'History ID',
        type: 'integer',
        isPrimaryKey: true,
        readOnly: true,
      });
      fields.push({
        name: 'changedDttm',
        label: 'Changed Date/Time',
        type: 'varchar',
        readOnly: true,
        placeholder: 'System Generated on update',
      });
    }

    return fields;
  }, [tickets, users, formMode]);

  // Table Columns Definition
  const columns = [
    { key: 'historyID', label: 'History ID', sortable: true, width: '100px' },
    {
      key: 'ticketID',
      label: 'Ticket #',
      sortable: true,
      render: (row) => {
        const ticket = tickets.find((t) => Number(t.ticketID) === Number(row.ticketID));
        return ticket ? ticket.ticketNumber : `ID: ${row.ticketID}`;
      },
    },
    {
      key: 'fromStatusID',
      label: 'From Status',
      sortable: true,
      render: (row) => {
        const status = mockStatuses.find((s) => Number(s.statusID) === Number(row.fromStatusID));
        return status ? <StatusBadge value={status.statusName} /> : 'Unknown';
      },
    },
    {
      key: 'toStatusID',
      label: 'To Status',
      sortable: true,
      render: (row) => {
        const status = mockStatuses.find((s) => Number(s.statusID) === Number(row.toStatusID));
        return status ? <StatusBadge value={status.statusName} /> : 'Unknown';
      },
    },
    {
      key: 'changedBy',
      label: 'Changed By',
      sortable: true,
      render: (row) => {
        const user = users.find((u) => Number(u.userID) === Number(row.changedBy));
        return user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username : `User ID: ${row.changedBy}`;
      },
    },
    {
      key: 'changedDttm',
      label: 'Changed At',
      sortable: true,
      render: (row) => (row.changedDttm ? new Date(row.changedDttm).toLocaleString() : '-'),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Ticket Status History</h1>
          <div className="subtitle">
            Full CRUD audit log tracking transitions between ticket workflow statuses.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add Status Entry
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="historyID"
        emptyMessage="No history logs found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal for Create & Edit */}
      {isFormOpen && (
        <TableForm
          fields={historyFields}
          initialValues={
            selectedRecord || {
              ticketID: '',
              fromStatusID: '',
              toStatusID: '',
              changedBy: '',
            }
          }
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Status Entry"
        />
      )}

      {/* Confirmation Delete Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Status History Record"
        itemLabel={selectedRecord ? `History ID: ${selectedRecord.historyID}` : 'Entry'}
        deleting={deleting}
      />
    </div>
  );
}
